import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Tv2, Film, Compass, ArrowLeft, Loader2, Youtube, RefreshCw,
  Download, Trash2, Wand2, FolderOpen, Lightbulb, Sparkles, Image as ImageIcon, X, Bookmark, BookmarkCheck, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import api, { imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ElapsedTimer } from "@/components/ElapsedTimer";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const full = (u) => (u ? `${BACKEND}${u}` : null);

function friendlyError(msg) {
  const low = (msg || "").toLowerCase();
  if (low.includes("out of credits") || low.includes("insufficient") || low.includes("balance")) {
    if (low.includes("runway")) return "Your Runway account is out of credits. Top it up, then try again.";
    return "Your video provider is out of credits. Add funds to that account, then try again.";
  }
  if (low.includes("atlas") || low.includes("assetd_sync_jobs") || low.includes("queued model history")) {
    return "The video provider (Seedance/Atlas) is temporarily unavailable — please wait a few minutes and try again.";
  }
  if (low.includes("runwayml_api_secret") || low.includes("runway isn't enabled")) {
    return "Runway isn't enabled on this server yet. Deploy the latest version, then try again.";
  }
  return msg || "Something went wrong. Please try again.";
}

export default function FunnyAnimals() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [idea, setIdea] = useState("");
  const [imaging, setImaging] = useState(false);
  const [imageId, setImageId] = useState(null);
  const [model, setModel] = useState("1.5");
  const [duration, setDuration] = useState(10);
  const [resolution, setResolution] = useState("720p");
  const [generating, setGenerating] = useState(false);
  const [cancelJobId, setCancelJobId] = useState(null);
  const [history, setHistory] = useState([]);
  const [lastVideo, setLastVideo] = useState(null); // video made for the current image (if any)
  const [loadedStoryId, setLoadedStoryId] = useState(null); // story currently open in the editor (for update-in-place)
  const [vdlg, setVdlg] = useState(null); // regenerate-video dialog: { prompt, model, duration, resolution }
  const [savedStories, setSavedStories] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/funny-animals/history");
      setHistory(data.items || []);
    } catch { /* optional */ }
  }, []);
  const loadStories = useCallback(async () => {
    try {
      const { data } = await api.get("/funny-animals/stories");
      setSavedStories(data.items || []);
    } catch { /* optional */ }
  }, []);
  useEffect(() => { loadHistory(); loadStories(); }, [loadHistory, loadStories]);

  const generateImage = async () => {
    if (!idea.trim()) { toast.error("Enter a funny idea first"); return; }
    setImaging(true);
    setImageId(null);
    try {
      const { data } = await api.post("/funny-animals/generate-image", { idea: idea.trim() });
      const jobId = data.job_id;
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      let id = null;
      for (let i = 0; i < 60; i++) {
        await sleep(3000);
        const res = await api.get(`/image-job/${jobId}`);
        if (res.data.status === "done") { id = res.data.image_id; break; }
        if (res.data.status === "error") throw new Error(res.data.error || "Image generation failed");
      }
      if (!id) throw new Error("Image generation timed out");
      setImageId(id);
      setLastVideo(null); // new image → no video for it yet
      toast.success("Image ready — now pick a video service");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Image generation failed");
    } finally {
      setImaging(false);
    }
  };

  const runVideo = async (promptText, m, d, r) => {
    if (!imageId) { toast.error("Generate an image first"); return; }
    setGenerating(true);
    let jobId = null;
    try {
      const { data } = await api.post("/funny-animals/generate-video", {
        image_id: imageId, idea: (promptText ?? "").trim(), model: m, duration: d, resolution: r,
      });
      jobId = data.job_id;
      setCancelJobId(jobId);
      const sleep = (ms) => new Promise((r2) => setTimeout(r2, ms));
      let url = null;
      for (let i = 0; i < 170; i++) {
        await sleep(5000);
        try {
          const res = await api.get(`/video-job/${jobId}`);
          if (res.data.status === "done") { url = res.data.video_url; break; }
          if (res.data.status === "cancelled") throw new Error("Cancelled by user.");
          if (res.data.status === "error") throw new Error(friendlyError(res.data.error));
        } catch (e) {
          if (e?.message && !e?.response) throw e;
        }
      }
      if (!url) throw new Error("Video generation timed out");
      setLastVideo({ video_url: url, model: m, duration: d, resolution: r });
      toast.success("Funny animal short ready!");
      await loadHistory();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Video generation failed";
      if (!/cancelled by user/i.test(msg)) toast.error(friendlyError(msg));
    } finally {
      setGenerating(false);
      setCancelJobId(null);
    }
  };

  const generateVideo = () => runVideo(idea, model, duration, resolution);

  const openVideoRegen = () => setVdlg({ prompt: idea, model, duration, resolution });
  const confirmVideoRegen = () => {
    const v = vdlg;
    setVdlg(null);
    setIdea(v.prompt);
    setModel(v.model);
    setDuration(v.duration);
    setResolution(v.resolution);
    runVideo(v.prompt, v.model, v.duration, v.resolution);
  };

  const cancelVideo = async () => {
    if (!cancelJobId) return;
    try {
      await api.post(`/video-job/${cancelJobId}/cancel`);
      toast.info("Cancelling…");
    } catch { /* ignore */ }
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/funny-animals/history/${id}`);
      setHistory((h) => h.filter((x) => x.item_id !== id));
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
  };

  const saveStory = async () => {
    if (!imageId) { toast.error("Generate an image first"); return; }
    setSaving(true);
    try {
      const payload = { idea: idea.trim(), image_id: imageId };
      if (loadedStoryId) payload.story_id = loadedStoryId;
      if (lastVideo) Object.assign(payload, lastVideo);
      const { data } = await api.post("/funny-animals/stories", payload);
      setSavedStories((s) => [data, ...s.filter((x) => x.story_id !== data.story_id)]);
      setLoadedStoryId(data.story_id);
      toast.success(loadedStoryId ? "Story updated" : "Story saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save story");
    } finally {
      setSaving(false);
    }
  };

  const removeStory = async (id) => {
    try {
      await api.delete(`/funny-animals/stories/${id}`);
      setSavedStories((s) => s.filter((x) => x.story_id !== id));
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
  };

  const loadStory = (st) => {
    if (generating || imaging) { toast.info("Please wait for the current generation to finish"); return; }
    setLoadedStoryId(st.story_id);
    setIdea(st.idea || "");
    setImageId(st.image_id || null);
    setLastVideo(st.video_url ? { video_url: st.video_url, model: st.model, duration: st.duration, resolution: st.resolution } : null);
    if (st.model) setModel(st.model);
    if (st.duration) setDuration(st.duration);
    if (st.resolution) setResolution(st.resolution);
    toast.info("Loaded saved story");
  };

  const loadItem = (it) => {
    if (generating || imaging) { toast.info("Please wait for the current generation to finish"); return; }
    setLoadedStoryId(null); // history shorts aren't saved stories; saving creates a new keeper
    setIdea(it.idea || "");
    setImageId(it.image_id || null);
    setLastVideo(it.video_url ? { video_url: it.video_url, model: it.model, duration: it.duration, resolution: it.resolution } : null);
    if (it.model) setModel(it.model);
    if (it.duration) setDuration(it.duration);
    if (it.resolution) setResolution(it.resolution);
    if (!it.image_id) toast.warning("This clip has no source image saved, so it can't be regenerated — but you can still view/download it.");
    else toast.info("Loaded — hit Regenerate video to remake it");
  };

  const newStory = () => {
    if (generating || imaging) { toast.info("Please wait for the current generation to finish"); return; }
    setLoadedStoryId(null);
    setIdea("");
    setImageId(null);
    setLastVideo(null);
    setModel("1.5");
    setDuration(10);
    setResolution("720p");
    toast.info("Started a new funny animal story");
  };

  const NavBtn = ({ icon: Icon, label, testid, onClick, active, indent }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-md py-2 text-sm transition-colors ${indent ? "pl-9 pr-3" : "px-3"} ${
        active ? "bg-[#1d2028] text-[#f3f4f6] border border-[#00e5ff]/40" : "text-[#9ca3af] hover:bg-[#14161c] hover:text-[#f3f4f6] border border-transparent"
      }`}
    >
      <Icon className={`h-4 w-4 shrink-0 ${active ? "text-[#00e5ff]" : ""}`} /> {label}
    </button>
  );

  const modelHint = model === "grok" ? "Grok Imagine · native audio · up to 15s"
    : model === "runway" ? "Runway Gen-4 · 5s or 10s · no audio"
    : model === "runway_seedance" ? "Runway · Seedance 2.5 · 4–30s · 480p/720p"
    : model === "runway_hailuo" ? "Runway · MiniMax Hailuo 3 · 5–15s · 768P/2K"
    : model === "2.5" ? "Seedance 2.5 · native audio · up to 30s · 4K"
    : model === "2.0" ? "Seedance 2.0 · voice + lip-sync · up to 15s"
    : "Seedance 1.5 · voice + lip-sync · up to 12s";

  return (
    <div className="grain flex h-screen overflow-hidden bg-[#0b0c10] text-[#f3f4f6]">
      {/* Left rail */}
      <aside className="flex h-full w-72 shrink-0 flex-col border-r border-[#272a35] bg-[#0e1015]">
        <div className="flex items-center gap-2 px-5 h-16 border-b border-[#272a35]">
          <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-md bg-white">
            <img src={`${process.env.PUBLIC_URL}/ytf-logo.png`} alt="YouTube Factory" className="h-full w-full object-contain" />
          </div>
          <span className="font-display font-bold tracking-tight">YouTube Factory <span className="text-[#00e5ff]">AI</span></span>
        </div>
        <div className="p-3 space-y-1">
          <NavBtn icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" onClick={() => navigate("/dashboard")} />
          <NavBtn icon={Compass} label="Niches" testid="nav-niches" onClick={() => navigate("/niches")} />
          <NavBtn icon={Tv2} label="Channels" testid="nav-channels" onClick={() => navigate("/channels")} />
        </div>
        <div className="px-3">
          <div className="flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-[#f3f4f6]">
            <FolderOpen className="h-4 w-4 text-[#9ca3af]" /> Content
          </div>
          <NavBtn icon={Film} label="Studio" testid="nav-studio" indent onClick={() => navigate("/studio")} />
          <NavBtn icon={Lightbulb} label="Story Ideas" testid="nav-story-ideas" indent onClick={() => toast.info("Story Ideas is coming soon")} />
          <NavBtn icon={Sparkles} label="Funny Animals" testid="nav-funny-animals" indent active onClick={() => {}} />
          {(savedStories.length > 0 || history.length > 0) && (
            <div className="mb-1 max-h-[40vh] space-y-0.5 overflow-y-auto" data-testid="funny-saved-nav">
              {savedStories.map((st) => (
                <button
                  key={`s-${st.story_id}`}
                  data-testid={`funny-saved-nav-${st.story_id}`}
                  onClick={() => loadStory(st)}
                  title={st.title}
                  className="flex w-full items-center gap-2 rounded-md py-1.5 pl-[52px] pr-3 text-left text-xs text-[#9ca3af] transition-colors hover:bg-[#14161c] hover:text-[#f3f4f6]"
                >
                  {st.video_url
                    ? <Film className="h-3 w-3 shrink-0 text-[#f5b301]" />
                    : <ImageIcon className="h-3 w-3 shrink-0 text-[#f5b301]" />}
                  <span className="truncate">{st.title}</span>
                </button>
              ))}
              {history
                .filter((it) => !savedStories.some((s) => s.video_url && s.video_url === it.video_url))
                .map((it) => (
                  <button
                    key={`h-${it.item_id}`}
                    data-testid={`funny-short-nav-${it.item_id}`}
                    onClick={() => loadItem(it)}
                    title={it.idea}
                    className="flex w-full items-center gap-2 rounded-md py-1.5 pl-[52px] pr-3 text-left text-xs text-[#9ca3af] transition-colors hover:bg-[#14161c] hover:text-[#f3f4f6]"
                  >
                    <Film className="h-3 w-3 shrink-0 text-[#00e5ff]" />
                    <span className="truncate">{it.idea || "Untitled short"}</span>
                  </button>
                ))}
            </div>
          )}
          <NavBtn icon={RefreshCw} label="Regenerate" testid="nav-regenerate" indent onClick={() => navigate("/regenerate")} />
        </div>
        <div className="flex-1" />
        <div className="border-t border-[#272a35] p-4">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1d2028] text-[#00e5ff]"><Youtube className="h-4 w-4" /></div>
            )}
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium">{user?.name || "Signed in"}</p>
              <p className="truncate text-xs text-[#9ca3af]">{user?.email || ""}</p>
            </div>
            <button onClick={logout} title="Sign out" className="text-[#9ca3af] hover:text-red-400 transition-colors">
              <ArrowLeft className="h-4 w-4 rotate-180" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="z-20 flex h-16 shrink-0 items-center justify-between border-b border-[#272a35] bg-black/60 px-6 backdrop-blur-xl">
          <div>
            <p className="overline !text-white">Story Ideas</p>
            <h1 className="font-display text-base font-semibold tracking-tight leading-none mt-0.5">Funny Animals</h1>
          </div>
          <Button
            data-testid="funny-new-btn"
            onClick={newStory}
            className="rounded-md bg-[#a855f7] font-semibold text-white hover:bg-[#9333ea]"
          >
            <Plus className="mr-1.5 h-4 w-4" /> New
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="mx-auto max-w-4xl space-y-6">
            {/* Idea + image + controls */}
            <div className="rounded-lg border border-[#272a35] bg-[#0e1015] p-5" data-testid="funny-panel">
              <p className="text-sm text-[#9ca3af]">
                Type a funny idea. We generate a <span className="text-[#f3f4f6]">photorealistic 9:16 image</span>, then turn it into a
                viral <span className="text-[#f3f4f6]">TikTok / YouTube Shorts</span> video using any video service.
              </p>

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[220px_1fr]">
                {/* Image preview */}
                <div>
                  {imaging ? (
                    <div className="flex aspect-[9/16] items-center justify-center rounded-md border border-dashed border-[#272a35] bg-[#14161c]" data-testid="funny-image-loading">
                      <div className="flex flex-col items-center gap-2 text-[#9ca3af]">
                        <Loader2 className="h-6 w-6 animate-spin text-[#00e5ff]" />
                        <span className="text-xs">Creating image…</span>
                      </div>
                    </div>
                  ) : imageId ? (
                    <div className="space-y-3">
                      <div>
                        <p className="mb-1 text-[10px] uppercase tracking-wide text-[#6b7280]">Image</p>
                        <img
                          data-testid="funny-image-preview"
                          src={imageUrl(imageId)}
                          alt="Funny animal"
                          className="mx-auto aspect-[9/16] w-full rounded-md border border-[#272a35] bg-black object-cover"
                        />
                        <button
                          data-testid="funny-regenerate-image"
                          onClick={generateImage}
                          disabled={generating || imaging}
                          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md border border-[#272a35] px-2 py-1.5 text-xs text-[#9ca3af] hover:text-[#00e5ff] disabled:opacity-50"
                        >
                          <RefreshCw className="h-3.5 w-3.5" /> New image
                        </button>
                      </div>
                      {lastVideo && (
                        <div>
                          <p className="mb-1 text-[10px] uppercase tracking-wide text-[#6b7280]">Video</p>
                          <div className={`relative overflow-hidden rounded-md border bg-black ${generating ? "border-[#a855f7] ring-2 ring-[#a855f7] animate-pulse" : "border-[#272a35]"}`} data-testid="funny-video-player">
                            <video
                              key={lastVideo.video_url}
                              data-testid="funny-video-preview"
                              src={full(lastVideo.video_url)}
                              controls
                              playsInline
                              className="mx-auto aspect-[9/16] w-full object-contain"
                            />
                            {generating && (
                              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-black/75 backdrop-blur-sm" data-testid="funny-video-regenerating">
                                <Loader2 className="h-7 w-7 animate-spin text-[#a855f7]" />
                                <span className="text-xs font-bold uppercase tracking-wide text-[#a855f7]">Regenerating…</span>
                                <ElapsedTimer testid="funny-video-timer" className="font-mono text-sm font-bold tabular-nums text-[#a855f7]" />
                              </div>
                            )}
                            {!generating && (
                              <a
                                href={full(lastVideo.video_url)}
                                download="funny-animal.mp4"
                                data-testid="funny-video-download"
                                title="Download video"
                                className="absolute right-2 top-2 rounded bg-black/60 p-1.5 text-white transition-colors hover:bg-black/80"
                              >
                                <Download className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex aspect-[9/16] w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-[#272a35] bg-[#14161c] text-center text-[#9ca3af]" data-testid="funny-image-placeholder">
                      <ImageIcon className="h-7 w-7 text-[#6b7280]" />
                      <span className="px-4 text-[11px]">Your 9:16 image will appear here</span>
                    </div>
                  )}
                </div>

                {/* Idea + settings */}
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs text-[#9ca3af]">Funny idea</label>
                    <Textarea
                      data-testid="funny-idea-input"
                      value={idea}
                      onChange={(e) => setIdea(e.target.value)}
                      placeholder="e.g. A golden retriever wearing sunglasses confidently 'driving' a tiny toy car through the kitchen"
                      className="min-h-[96px] resize-y border-[#272a35] bg-[#14161c] text-[#f3f4f6] placeholder:text-[#6b7280]"
                    />
                  </div>
                  <Button
                    data-testid="funny-generate-image-btn"
                    onClick={generateImage}
                    disabled={imaging || !idea.trim()}
                    className="w-full rounded-md bg-[#a855f7] font-semibold text-white hover:bg-[#9333ea] disabled:opacity-50"
                  >
                    {imaging ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImageIcon className="mr-2 h-4 w-4" />}
                    {imaging ? "Generating image…" : imageId ? "Regenerate image" : "Generate image"}
                  </Button>

                  <div className="border-t border-[#272a35] pt-3">
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="mb-1 block text-[10px] uppercase tracking-wide text-[#6b7280]">Video service</label>
                        <select data-testid="funny-model" value={model} onChange={(e) => setModel(e.target.value)} className="w-full rounded-md border border-[#272a35] bg-[#14161c] px-2 py-2 text-sm text-[#f3f4f6]">
                          <option value="1.5">Seedance 1.5 Pro</option>
                          <option value="2.0">Seedance 2.0</option>
                          <option value="2.5">Seedance 2.5</option>
                          <option value="grok">Grok Imagine</option>
                          <option value="runway">Runway (Gen-4)</option>
                <option value="runway_seedance">Runway · Seedance 2.5</option>
                <option value="runway_hailuo">Runway · MiniMax 3</option>
                        </select>
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] uppercase tracking-wide text-[#6b7280]">Duration</label>
                        <select data-testid="funny-duration" value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="w-full rounded-md border border-[#272a35] bg-[#14161c] px-2 py-2 text-sm text-[#f3f4f6]">
                          {[10, 12, 15].map((d) => <option key={d} value={d}>{d}s</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="mb-1 block text-[10px] uppercase tracking-wide text-[#6b7280]">Resolution</label>
                        <select data-testid="funny-resolution" value={resolution} onChange={(e) => setResolution(e.target.value)} className="w-full rounded-md border border-[#272a35] bg-[#14161c] px-2 py-2 text-sm text-[#f3f4f6]">
                          {["480p", "720p", "1080p"].map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    </div>
                    <p className="mt-1.5 text-[10px] text-[#6b7280]" data-testid="funny-model-hint">{modelHint}</p>

                    {generating ? (
                      <div className="mt-3 flex gap-2">
                        <Button disabled className="flex-1 rounded-md bg-[#00e5ff]/60 font-semibold text-black">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating video…
                          <ElapsedTimer testid="funny-video-timer" className="ml-2 font-mono text-sm font-bold tabular-nums text-black" />
                        </Button>
                        <Button
                          data-testid="funny-cancel-btn"
                          onClick={cancelVideo}
                          variant="outline"
                          className="rounded-md border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <X className="mr-1 h-4 w-4" /> Cancel
                        </Button>
                      </div>
                    ) : lastVideo ? (
                      <Button
                        data-testid="funny-regenerate-video-btn"
                        onClick={openVideoRegen}
                        disabled={!imageId}
                        className="mt-3 w-full rounded-md bg-[#a855f7] font-semibold text-white hover:bg-[#9333ea] disabled:opacity-50"
                      >
                        <RefreshCw className="mr-2 h-4 w-4" /> Regenerate video
                      </Button>
                    ) : (
                      <Button
                        data-testid="funny-generate-video-btn"
                        onClick={generateVideo}
                        disabled={!imageId}
                        className="mt-3 w-full rounded-md bg-[#00e5ff] font-semibold text-black hover:bg-[#33ebff] disabled:opacity-50"
                      >
                        <Wand2 className="mr-2 h-4 w-4" /> Create video
                      </Button>
                    )}
                    {imageId && (
                      <Button
                        data-testid="funny-save-story-btn"
                        onClick={saveStory}
                        disabled={saving}
                        variant="outline"
                        className="mt-2 w-full rounded-md border-[#f5b301]/50 bg-[#f5b301]/10 font-semibold text-[#f5b301] hover:bg-[#f5b301]/20 disabled:opacity-50"
                        title={lastVideo ? "Save this idea, image and video as a story" : "Save this idea and image as a story (video optional)"}
                      >
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bookmark className="mr-2 h-4 w-4" />}
                        {saving ? "Saving…" : loadedStoryId ? "Update Story" : "Save Story"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Saved stories */}
            <div>
              <div className="mb-3 flex items-center gap-2 text-xs text-[#9ca3af]">
                <BookmarkCheck className="h-3.5 w-3.5 text-[#f5b301]" /> Saved stories ({savedStories.length})
              </div>
              {savedStories.length === 0 ? (
                <div className="rounded-lg border border-dashed border-[#272a35] bg-[#14161c] p-6 text-center text-sm text-[#9ca3af]" data-testid="funny-stories-empty">
                  Generate an image, then tap <span className="text-[#f5b301]">Save story</span> to keep it here.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4" data-testid="funny-stories">
                  {savedStories.map((st) => (
                    <div key={st.story_id} className="rounded-lg border border-[#f5b301]/30 bg-[#0e1015] p-2" data-testid={`funny-story-${st.story_id}`}>
                      {st.video_url ? (
                        <video src={full(st.video_url)} controls playsInline className="mb-2 aspect-[9/16] w-full rounded bg-black object-contain" />
                      ) : (
                        <img src={full(st.image_url)} alt={st.title} className="mb-2 aspect-[9/16] w-full rounded bg-black object-cover" />
                      )}
                      <p className="mb-2 line-clamp-2 text-[11px] font-semibold text-[#f3f4f6]" title={st.idea || st.title}>{st.title}</p>
                      <div className="mb-2 flex flex-wrap gap-1 text-[10px] text-[#6b7280]">
                        {st.video_url ? (
                          <>
                            <span className="rounded bg-[#14161c] px-1.5 py-0.5">{st.model === "grok" ? "Grok" : st.model === "runway" ? "Runway" : st.model === "runway_seedance" ? "Runway · Seedance 2.5" : st.model === "runway_hailuo" ? "Runway · MiniMax 3" : `Seedance ${st.model}`}</span>
                            {st.duration ? <span className="rounded bg-[#14161c] px-1.5 py-0.5">{st.duration}s</span> : null}
                            {st.resolution ? <span className="rounded bg-[#14161c] px-1.5 py-0.5">{st.resolution}</span> : null}
                          </>
                        ) : (
                          <span className="rounded bg-[#14161c] px-1.5 py-0.5">Image only</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        {st.video_url && (
                          <a
                            href={full(st.video_url)}
                            download={`funny-story-${st.story_id}.mp4`}
                            data-testid={`funny-story-download-${st.story_id}`}
                            className="flex flex-1 items-center justify-center gap-1 rounded-md border border-[#272a35] px-2 py-1.5 text-xs text-[#f3f4f6] hover:border-[#00e5ff]/50 hover:text-[#00e5ff]"
                          >
                            <Download className="h-3.5 w-3.5" /> Download
                          </a>
                        )}
                        <button
                          data-testid={`funny-story-delete-${st.story_id}`}
                          onClick={() => removeStory(st.story_id)}
                          className={`rounded-md border border-[#272a35] p-1.5 text-[#9ca3af] hover:border-red-500/50 hover:text-red-400 ${st.video_url ? "" : "flex-1 flex items-center justify-center gap-1 text-xs"}`}
                          title="Delete saved story"
                        >
                          <Trash2 className="h-3.5 w-3.5" />{!st.video_url && " Delete"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* History */}
            <div>
              <div className="mb-3 flex items-center gap-2 text-xs text-[#9ca3af]">
                <Sparkles className="h-3.5 w-3.5 text-[#f5b301]" /> Funny animal shorts ({history.length})
              </div>
              {history.length === 0 ? (
                <div className="rounded-lg border border-dashed border-[#272a35] bg-[#14161c] p-8 text-center text-sm text-[#9ca3af]" data-testid="funny-history-empty">
                  Your funny animal shorts will appear here.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4" data-testid="funny-history">
                  {history.map((it) => (
                    <div key={it.item_id} className="rounded-lg border border-[#272a35] bg-[#0e1015] p-2" data-testid={`funny-item-${it.item_id}`}>
                      <video src={full(it.video_url)} controls playsInline className="mb-2 aspect-[9/16] w-full rounded bg-black object-contain" />
                      <p className="mb-2 line-clamp-2 text-[11px] text-[#9ca3af]" title={it.idea}>{it.idea || <span className="italic">No idea</span>}</p>
                      <div className="mb-2 flex flex-wrap gap-1 text-[10px] text-[#6b7280]">
                        <span className="rounded bg-[#14161c] px-1.5 py-0.5">{it.model === "grok" ? "Grok" : it.model === "runway" ? "Runway" : it.model === "runway_seedance" ? "Runway · Seedance 2.5" : it.model === "runway_hailuo" ? "Runway · MiniMax 3" : `Seedance ${it.model}`}</span>
                        <span className="rounded bg-[#14161c] px-1.5 py-0.5">{it.duration}s</span>
                        <span className="rounded bg-[#14161c] px-1.5 py-0.5">{it.resolution}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <a
                          href={full(it.video_url)}
                          download={`funny-animal-${it.item_id}.mp4`}
                          data-testid={`funny-download-${it.item_id}`}
                          className="flex flex-1 items-center justify-center gap-1 rounded-md border border-[#272a35] px-2 py-1.5 text-xs text-[#f3f4f6] hover:border-[#00e5ff]/50 hover:text-[#00e5ff]"
                        >
                          <Download className="h-3.5 w-3.5" /> Download
                        </a>
                        <button
                          data-testid={`funny-delete-${it.item_id}`}
                          onClick={() => removeItem(it.item_id)}
                          className="rounded-md border border-[#272a35] p-1.5 text-[#9ca3af] hover:border-red-500/50 hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Regenerate video dialog — edit the prompt + settings, same as scene videos */}
      <Dialog open={!!vdlg} onOpenChange={(o) => !o && setVdlg(null)}>
        <DialogContent className="border-[#272a35] bg-[#14161c] text-[#f3f4f6]" data-testid="funny-video-regen-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Regenerate video</DialogTitle>
          </DialogHeader>
          <label className="mb-1 block text-xs text-[#9ca3af]">Prompt (edit what the animal does)</label>
          <Textarea
            data-testid="funny-video-regen-prompt"
            value={vdlg?.prompt ?? ""}
            onChange={(e) => setVdlg((v) => ({ ...v, prompt: e.target.value }))}
            placeholder="e.g. slower motion, the cat looks straight at the camera and winks, add gentle steam from the pan"
            className="min-h-[90px] resize-y border-[#272a35] bg-[#0e1015] text-[#f3f4f6] placeholder:text-[#6b7280]"
          />
          <div className="mt-2 grid grid-cols-3 gap-2">
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wide text-[#6b7280]">Video service</label>
              <select
                data-testid="funny-video-regen-model"
                value={vdlg?.model ?? "1.5"}
                onChange={(e) => setVdlg((v) => ({ ...v, model: e.target.value }))}
                className="w-full rounded border border-[#272a35] bg-[#0b0c10] px-2 py-1.5 text-xs text-[#f3f4f6]"
              >
                <option value="1.5">Seedance 1.5 Pro</option>
                <option value="2.0">Seedance 2.0</option>
                <option value="2.5">Seedance 2.5</option>
                <option value="grok">Grok Imagine</option>
                <option value="runway">Runway (Gen-4)</option>
                <option value="runway_seedance">Runway · Seedance 2.5</option>
                <option value="runway_hailuo">Runway · MiniMax 3</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wide text-[#6b7280]">Duration</label>
              <select
                data-testid="funny-video-regen-duration"
                value={vdlg?.duration ?? 10}
                onChange={(e) => setVdlg((v) => ({ ...v, duration: Number(e.target.value) }))}
                className="w-full rounded border border-[#272a35] bg-[#0b0c10] px-2 py-1.5 text-xs text-[#f3f4f6]"
              >
                {[10, 12, 15].map((d) => <option key={d} value={d}>{d}s</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wide text-[#6b7280]">Resolution</label>
              <select
                data-testid="funny-video-regen-resolution"
                value={vdlg?.resolution ?? "720p"}
                onChange={(e) => setVdlg((v) => ({ ...v, resolution: e.target.value }))}
                className="w-full rounded border border-[#272a35] bg-[#0b0c10] px-2 py-1.5 text-xs text-[#f3f4f6]"
              >
                {["480p", "720p", "1080p"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button
              data-testid="funny-video-regen-cancel"
              variant="outline"
              onClick={() => setVdlg(null)}
              className="border-[#272a35] text-[#9ca3af] hover:text-[#f3f4f6]"
            >
              Cancel
            </Button>
            <Button
              data-testid="funny-video-regen-confirm"
              onClick={confirmVideoRegen}
              className="bg-[#a855f7] font-semibold text-white hover:bg-[#9333ea]"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
